import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { Target, TrendingUp, DollarSign, Users, Save, CheckCircle2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const KPIConfiguration = () => {
    const { settings, updateSetting, loading } = useOps();
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);

    // Local state initialized from settingscontext
    const [targets, setTargets] = useState({
        monthly_sales_target: settings.find(s => s.setting_key === 'monthly_sales_target')?.setting_value || '',
        monthly_leads_target: settings.find(s => s.setting_key === 'monthly_leads_target')?.setting_value || '',
        conversion_rate_target: settings.find(s => s.setting_key === 'conversion_rate_target')?.setting_value || '',
        pass_rate_target: settings.find(s => s.setting_key === 'pass_rate_target')?.setting_value || ''
    });

    const handleChange = (key, val) => {
        setTargets(prev => ({ ...prev, [key]: val }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await Promise.all(
                Object.entries(targets).map(([key, value]) => updateSetting(key, value))
            );
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error("Failed to save KPIs", err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Performance Targets</h2>
                    <p className="page-subtitle">Configure Key Performance Indicators for the dashboard</p>
                </div>
            </div>

            <div className="max-w-3xl mx-auto fade-in">
                <form onSubmit={handleSave} className="card p-8 space-y-8">
                    <div className="border-b pb-4 mb-4 flex items-center gap-3 text-slate-700">
                        <Target size={24} className="text-indigo-600" />
                        <h3 className="text-xl font-bold">Monthly Goals</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Financial Target */}
                        <div className="form-group">
                            <label className="flex items-center gap-2 font-bold text-slate-700 mb-2">
                                <DollarSign size={16} className="text-emerald-500" /> Revenue Target (USD)
                            </label>
                            <input
                                type="number"
                                className="form-input text-lg font-mono"
                                placeholder="e.g. 50000"
                                value={targets.monthly_sales_target}
                                onChange={e => handleChange('monthly_sales_target', e.target.value)}
                            />
                            <p className="text-xs text-slate-400 mt-1">Expected monthly income from all branches.</p>
                        </div>

                        {/* Lead Volume */}
                        <div className="form-group">
                            <label className="flex items-center gap-2 font-bold text-slate-700 mb-2">
                                <Users size={16} className="text-blue-500" /> Lead Volume Target
                            </label>
                            <input
                                type="number"
                                className="form-input text-lg font-mono"
                                placeholder="e.g. 200"
                                value={targets.monthly_leads_target}
                                onChange={e => handleChange('monthly_leads_target', e.target.value)}
                            />
                            <p className="text-xs text-slate-400 mt-1">Target number of new inquiries per month.</p>
                        </div>

                        {/* Conversion Rate */}
                        <div className="form-group">
                            <label className="flex items-center gap-2 font-bold text-slate-700 mb-2">
                                <TrendingUp size={16} className="text-purple-500" /> Conversion Rate (%)
                            </label>
                            <input
                                type="number"
                                className="form-input text-lg font-mono"
                                placeholder="e.g. 25"
                                max="100"
                                value={targets.conversion_rate_target}
                                onChange={e => handleChange('conversion_rate_target', e.target.value)}
                            />
                            <p className="text-xs text-slate-400 mt-1">Percentage of leads to convert to students.</p>
                        </div>

                        {/* Pass Rate */}
                        <div className="form-group">
                            <label className="flex items-center gap-2 font-bold text-slate-700 mb-2">
                                <CheckCircle2 size={16} className="text-orange-500" /> Student Pass Rate (%)
                            </label>
                            <input
                                type="number"
                                className="form-input text-lg font-mono"
                                placeholder="e.g. 85"
                                max="100"
                                value={targets.pass_rate_target}
                                onChange={e => handleChange('pass_rate_target', e.target.value)}
                            />
                            <p className="text-xs text-slate-400 mt-1">Target success rate for road tests.</p>
                        </div>
                    </div>

                    <div className="pt-6 border-t flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className={`btn btn-primary px-8 py-3 flex items-center gap-3 text-lg transition-all ${success ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                        >
                            {saving ? <LoadingSpinner size={20} color="white" /> : success ? <CheckCircle2 size={20} /> : <Save size={20} />}
                            {saving ? 'Saving...' : success ? 'Saved Successfully!' : 'Update Targets'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default KPIConfiguration;
